import React from 'react';
import styled from 'styled-components';

export type TariffRateView = 'import' | 'export';

interface SocChartTogglesProps {
  showBatteryActions: boolean;
  onToggleBatteryActions: () => void;
  showSolarGeneration: boolean;
  onToggleSolarGeneration: () => void;
  showTariffRates: boolean;
  onToggleTariffRates: () => void;
  importExportRatesDiffer: boolean;
  tariffRateView: TariffRateView;
  onTariffRateViewChange: (view: TariffRateView) => void;
  importHasNoShading?: boolean;
  exportHasNoShading?: boolean;
  inline?: boolean;
}

const ToggleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
`;

const CenterRow = styled(Row)`
  justify-content: center;
  min-height: 42px;
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: ${(props) => props.theme.colors.black};
  user-select: none;
  margin-bottom: 0;
`;

const Checkbox = styled.input`
  width: 22px;
  height: 22px;
  accent-color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
  margin: 0;
`;

// Figma pill segmented control (320×42)
const SegmentedControl = styled.div`
  display: inline-flex;
  background: ${(props) => props.theme.colors.extraLight};
  border-radius: 50px;
  height: 42px;
  width: fit-content;
  max-width: 100%;
`;

const TooltipWrapper = styled.span<{ $tooltip?: string }>`
  position: relative;
  display: inline-flex;

  ${(props) =>
    props.$tooltip
      ? `
    &::after {
      content: attr(data-tooltip);
      position: absolute;
      bottom: calc(100% + 6px);
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.78);
      color: #fff;
      font-size: 12px;
      line-height: 1.4;
      white-space: nowrap;
      padding: 5px 9px;
      border-radius: 4px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s;
      z-index: 1100;
    }
    &:hover::after {
      opacity: 1;
    }
  `
      : ''}
`;

const Segment = styled.button<{ $active: boolean }>`
  flex: 0 0 auto;
  height: 42px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  color: ${(props) => props.theme.colors.black};
  font-weight: 400;
  white-space: nowrap;
  background: transparent;
  border: 2px solid transparent;
  border-radius: 50px;

  ${(props) =>
    props.$active
      ? `
    background: ${props.theme.colors.white};
    border-color: ${props.theme.colors.primary};
  `
      : ''}

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/**
 * Toggle checkboxes and segmented control for SOC chart.
 * - Show battery actions (colour-coded segments)
 * - Show solar generation
 * - Show tariff rates (background shading)
 * - When tariff rates shown: switch between "Show import rates" and "Show export rates"
 */
const SocChartToggles: React.FC<SocChartTogglesProps> = ({
  showBatteryActions,
  onToggleBatteryActions,
  showSolarGeneration,
  onToggleSolarGeneration,
  showTariffRates,
  onToggleTariffRates,
  importExportRatesDiffer,
  tariffRateView,
  onTariffRateViewChange,
  importHasNoShading = false,
  exportHasNoShading = false,
  inline = false,
}) => {
  const checkboxes = (
    <>
      <ToggleLabel>
        <Checkbox type="checkbox" checked={showBatteryActions} onChange={onToggleBatteryActions} />
        Show battery actions
      </ToggleLabel>
      <ToggleLabel>
        <Checkbox type="checkbox" checked={showSolarGeneration} onChange={onToggleSolarGeneration} />
        Show solar generation
      </ToggleLabel>
      <ToggleLabel>
        <Checkbox type="checkbox" checked={showTariffRates} onChange={onToggleTariffRates} />
        Show tariff rates
      </ToggleLabel>
    </>
  );

  const importTooltip = importHasNoShading
    ? 'Import rates are consistent all day, so there are no shaded periods for cheap or peak rates.'
    : undefined;
  const exportTooltip = exportHasNoShading
    ? 'Export rates are consistent all day, so there are no shaded periods for cheap or peak rates.'
    : undefined;

  const segmentedControl = importExportRatesDiffer && (
    <SegmentedControl style={inline ? { visibility: showTariffRates ? 'visible' : 'hidden' } : undefined}>
      <TooltipWrapper $tooltip={importTooltip} data-tooltip={importTooltip}>
        <Segment
          type="button"
          $active={tariffRateView === 'import'}
          disabled={importHasNoShading}
          onClick={() => onTariffRateViewChange('import')}
        >
          Show import rates
        </Segment>
      </TooltipWrapper>
      <TooltipWrapper $tooltip={exportTooltip} data-tooltip={exportTooltip}>
        <Segment
          type="button"
          $active={tariffRateView === 'export'}
          disabled={exportHasNoShading}
          onClick={() => onTariffRateViewChange('export')}
        >
          Show export rates
        </Segment>
      </TooltipWrapper>
    </SegmentedControl>
  );

  if (inline) {
    return (
      <CenterRow>
        {checkboxes}
        {segmentedControl}
      </CenterRow>
    );
  }

  return (
    <ToggleContainer>
      <CenterRow>{checkboxes}</CenterRow>
      {showTariffRates && segmentedControl && <CenterRow>{segmentedControl}</CenterRow>}
    </ToggleContainer>
  );
};

export default SocChartToggles;
